import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import {
  clampChatPaneWidth,
  DEFAULT_CHAT_PANE_WIDTH,
} from "./chatPaneWidth";

export const useResizableChatPane = () => {
  const [width, setWidth] = useState(() =>
    typeof window === "undefined"
      ? DEFAULT_CHAT_PANE_WIDTH
      : clampChatPaneWidth(DEFAULT_CHAT_PANE_WIDTH, window.innerWidth),
  );
  const draggingRef = useRef(false);

  const finishDragging = useCallback(() => {
    draggingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      draggingRef.current = true;
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!draggingRef.current) return;
        const nextWidth = window.innerWidth - moveEvent.clientX;
        setWidth(clampChatPaneWidth(nextWidth, window.innerWidth));
      };
      const handleMouseUp = () => {
        finishDragging();
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [finishDragging],
  );

  useEffect(() => {
    const handleResize = () => {
      setWidth((currentWidth) =>
        clampChatPaneWidth(currentWidth, window.innerWidth),
      );
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => finishDragging, [finishDragging]);

  return { width, handleMouseDown };
};
